import React, { useState, useEffect, useMemo } from 'react';
import {
  MessageCircle, Search, CheckCircle2, XCircle, Loader2, Trash2,
  ShieldCheck, Phone, X, ExternalLink, Copy, Plus, ImageOff,
  Radio, AlertTriangle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import {
  whatsappCloudService,
  WhatsAppCloudStatus,
  WhatsAppCloudTestResult,
  WhatsAppCloudTemplate,
  WhatsAppWebhookHealth,
  WhatsAppIntegrationError,
  TemplateUsage,
  isMediaHeader,
  needsHeaderMedia,
} from '@/services/integrations/whatsappCloudService';
import AddTemplateModal from './_components/AddTemplateModal';
import HeaderMediaPanel from './_components/HeaderMediaPanel';
import SampleValuesPanel from './_components/SampleValuesPanel';
import TemplateTable from './_components/TemplateTable';
import { TEMPLATE_STATUS_BADGE, formatTemplateDate, renderTemplateBody, WA_BUBBLE_BG } from '@/utils/whatsappTemplate';
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';

const QUALITY_DOT: Record<string, string> = {
  GREEN: 'bg-green-500',
  YELLOW: 'bg-amber-500',
  RED: 'bg-red-500',
};

const WhatsAppCloudPage: React.FC = () => {
  const [status, setStatus] = useState<WhatsAppCloudStatus | null>(null);
  const [test, setTest] = useState<WhatsAppCloudTestResult | null>(null);
  const [templates, setTemplates] = useState<WhatsAppCloudTemplate[]>([]);
  const [usage, setUsage] = useState<Record<string, TemplateUsage>>({});
  const [webhookHealth, setWebhookHealth] = useState<WhatsAppWebhookHealth | null>(null);
  const [integrationErrors, setIntegrationErrors] = useState<WhatsAppIntegrationError[]>([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const [search, setSearch] = useState('');
  const [onlyApproved, setOnlyApproved] = useState(true);
  const [onlyNeedsMedia, setOnlyNeedsMedia] = useState(false);
  const [preview, setPreview] = useState<WhatsAppCloudTemplate | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [st, tpl, health, errors, use] = await Promise.all([
        whatsappCloudService.getStatus(),
        whatsappCloudService.getTemplates(),
        whatsappCloudService.getWebhookHealth(),
        whatsappCloudService.getIntegrationErrors(),
        // Stats are decoration — a failure here must not blank the template list.
        whatsappCloudService.getTemplateUsage().catch(() => ({} as Record<string, TemplateUsage>)),
      ]);
      setStatus(st);
      setTemplates(tpl);
      setUsage(use);
      setWebhookHealth(health);
      setIntegrationErrors(errors);
    } catch (e) {
      console.error('Failed to load WhatsApp Cloud data', e);
      toast.error('Failed to load WhatsApp Cloud data');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await whatsappCloudService.testConnection();
      setTest(res);
      if (res.ok) toast.success('Connection OK');
      else toast.error(res.error || 'Connection failed');
    } finally {
      setTesting(false);
    }
  };

  /** Refresh the list after an in-drawer edit, keeping the drawer on the same template. */
  const reloadTemplates = async () => {
    const tpl = await whatsappCloudService.getTemplates();
    setTemplates(tpl);
    setPreview((cur) => (cur ? tpl.find((t) => t.id === cur.id) || null : cur));
  };

  const closePreview = () => {
    setPreview(null);
    setConfirmDelete(false);
  };

  const handleDelete = async (t: WhatsAppCloudTemplate) => {
    setDeleting(true);
    try {
      await whatsappCloudService.deleteTemplate(t.id);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      closePreview();
      toast.success(`Deleted ${t.element_name}`);
    } catch (e: any) {
      // 409 carries the journeys still using it — show the API's message as-is.
      toast.error(e?.response?.data?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied');
  };

  const filtered = useMemo(() => {
    let list = templates;
    if (onlyApproved) list = list.filter((t) => t.status === 'APPROVED');
    if (onlyNeedsMedia) list = list.filter(needsHeaderMedia);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((t) => t.element_name.toLowerCase().includes(q) || (t.body || '').toLowerCase().includes(q));
    return list;
  }, [templates, onlyApproved, onlyNeedsMedia, search]);

  const counts = useMemo(() => ({
    total: templates.length,
    approved: templates.filter((t) => t.status === 'APPROVED').length,
    pending: templates.filter((t) => t.status === 'PENDING').length,
    rejected: templates.filter((t) => t.status === 'REJECTED').length,
    paused: templates.filter((t) => t.status === 'PAUSED').length,
    needsMedia: templates.filter(needsHeaderMedia).length,
  }), [templates]);

  const formatAge = (seconds: number | null): string => {
    if (seconds == null) return '—';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  /** Patch one template in place — avoids a full reload after attaching media. */
  const patchTemplate = (id: number, url: string | null) => {
    setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, header_media_url: url } : t)));
    setPreview((p) => (p && p.id === id ? { ...p, header_media_url: url } : p));
  };

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="WhatsApp"
        description="Connection, webhook health and approved message templates for the Meta WhatsApp Cloud API."
        icon={<WhatsAppIcon className="w-6 h-6" />}
      />

      <div className="p-6 space-y-6">
        {/* Connection status card */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 w-10 h-10 rounded-full flex items-center justify-center ${status?.configured ? 'bg-green-50' : 'bg-gray-100'}`}>
                {status?.configured
                  ? <ShieldCheck className="w-5 h-5 text-green-600" />
                  : <XCircle className="w-5 h-5 text-gray-400" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">Meta WhatsApp Cloud API</h3>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${status?.configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {status?.configured ? 'Configured' : 'Not configured'}
                  </span>
                </div>
                <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                  <div>Phone ID: <span className="font-mono text-gray-700">{status?.phone_number_id || '—'}</span></div>
                  <div>WABA ID: <span className="font-mono text-gray-700">{status?.waba_id || '—'}</span></div>
                  <div>Graph API: <span className="font-mono text-gray-700">{status?.graph_version || '—'}</span></div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleTest}
                disabled={testing || !status?.configured}
                className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Test connection
              </button>
              <button
                onClick={() => setAdding(true)}
                disabled={!status?.configured}
                className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-brandColor text-white hover:opacity-90 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                Add template
              </button>
            </div>
          </div>

          {/* Test result */}
          {test && (
            <div className={`mt-4 rounded-lg border p-3 text-sm ${test.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              {test.ok ? (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-gray-700">
                  <span className="flex items-center gap-1.5 font-medium text-green-700">
                    <CheckCircle2 className="w-4 h-4" /> Connected
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                    {test.phone?.verified_name} · {test.phone?.display_phone_number}
                  </span>
                  {test.phone?.quality_rating && (
                    <span className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${QUALITY_DOT[test.phone.quality_rating] || 'bg-gray-400'}`} />
                      {test.phone.quality_rating}
                    </span>
                  )}
                  {test.phone?.messaging_limit_tier && (
                    <span>Limit: {test.phone.messaging_limit_tier.replace(/^TIER_/, '').replace('_', ' ')}/24h</span>
                  )}
                  <span>WABA: {test.waba?.name} ({test.waba?.review_status})</span>
                </div>
              ) : (
                <span className="flex items-center gap-1.5 text-red-700">
                  <XCircle className="w-4 h-4" /> {test.error || 'Connection failed'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Webhook health + Recent integration errors */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100">
              <Radio className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 flex-1">Webhook health</h3>
              {webhookHealth && webhookHealth.failed_24h > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700">
                  {webhookHealth.failed_24h} failed
                </span>
              )}
            </div>
            <div className="p-5">
              {webhookHealth ? (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{webhookHealth.events_24h}</div>
                    <div className="text-xs text-gray-500 mt-0.5">events / 24h</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{formatAge(webhookHealth.seconds_since_last_event)}</div>
                    <div className="text-xs text-gray-500 mt-0.5">since last event</div>
                  </div>
                  <div>
                    <div className={`text-2xl font-bold ${webhookHealth.failed_24h > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {webhookHealth.failed_24h}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">failed to process</div>
                  </div>
                  <div className="col-span-3 pt-3 mt-1 border-t border-gray-100">
                    <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Fields this endpoint handles
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {webhookHealth.subscribed_fields.map((f) => (
                        <span key={f} className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-gray-100 text-gray-700">
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400">No webhook activity recorded yet.</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100">
              <AlertTriangle className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 flex-1">Recent integration errors</h3>
              <span className="text-xs text-gray-400">{integrationErrors.length} shown</span>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {integrationErrors.length === 0 ? (
                <p className="text-sm text-gray-400 p-5">No errors in the recent log — good sign.</p>
              ) : (
                integrationErrors.map((err) => (
                  <div key={err.id} className="flex items-start gap-3 px-5 py-3 border-b border-gray-50 last:border-0">
                    <span className="text-[11px] font-mono text-gray-400 pt-0.5 whitespace-nowrap">
                      {err.timestamp ? new Date(err.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-800 truncate">{err.message}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {err.kind === 'webhook' ? 'Webhook' : 'Send'}{err.code ? ` · ${err.code}` : ''}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Templates section */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Templates</h3>
            {/* The WABA is shared with marketing and carries ~190 approved templates;
                the CRM lists only the crm_* ones. */}
            <p className="text-[11.5px] text-gray-500 mt-0.5">
              Only templates named <code className="font-mono text-gray-700">crm_…</code> are
              used in the CRM. Marketing templates on the same WhatsApp account are not
              listed here, in the inbox, or in journeys.
            </p>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="text-gray-500"><b className="text-gray-900 font-semibold">{counts.approved}</b> approved</span>
            <span className="text-gray-500"><b className="text-amber-700 font-semibold">{counts.pending}</b> pending</span>
            <span className="text-gray-500"><b className="text-red-700 font-semibold">{counts.rejected}</b> rejected</span>
            <span className="text-gray-500"><b className="text-amber-700 font-semibold">{counts.paused}</b> paused</span>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 flex-1 min-w-[240px] max-w-md">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates…"
              className="flex-1 text-sm text-gray-900 border-0 outline-none bg-transparent"
            />
          </div>
          <button
            onClick={() => setOnlyApproved((v) => !v)}
            className={`px-3 py-2 text-sm rounded-lg border ${onlyApproved ? 'border-brandColor text-brandColor bg-green-50' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >
            Approved only
          </button>
          {counts.needsMedia > 0 && (
            <button
              onClick={() => setOnlyNeedsMedia((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border ${
                onlyNeedsMedia
                  ? 'border-amber-400 text-amber-800 bg-amber-50'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
              title="Media-header templates journeys cannot send until an asset is attached"
            >
              <ImageOff className="w-4 h-4" />
              Needs media ({counts.needsMedia})
            </button>
          )}
          <div className="text-xs text-gray-500 ml-auto">
            {filtered.length} shown · {counts.approved} approved / {counts.total} total
          </div>
        </div>

        {/* Templates grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor" />
            <p className="mt-4 text-gray-500">Loading templates…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <MessageCircle className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {templates.length === 0
                ? 'No crm_ templates yet — click “Add template” to create one.'
                : 'No templates match your search.'}
            </p>
          </div>
        ) : (
          <TemplateTable templates={filtered} usage={usage} onSelect={setPreview} />
        )}
      </div>

      {/* existingNames now carries only the crm_* names, so it catches a same-page
          collision only — the authoritative duplicate check runs server-side in
          create_template against every template on the WABA. */}
      {adding && (
        <AddTemplateModal
          onClose={() => setAdding(false)}
          onCreated={() => {
            // A new template lands as PENDING — drop the approved-only filter so
            // the user actually sees what they just submitted.
            setOnlyApproved(false);
            loadAll();
          }}
          existingNames={templates.map((t) => t.element_name)}
        />
      )}

      {/* Preview drawer */}
      {preview && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={closePreview}>
          <div className="w-full max-w-md h-full bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-900 truncate">{preview.element_name}</h3>
                  <span className={`px-2 py-0.5 text-[11px] font-medium rounded ${TEMPLATE_STATUS_BADGE[preview.status] || 'bg-gray-100 text-gray-600'}`}>
                    {preview.status}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5 uppercase">{preview.language}{preview.category ? ` · ${preview.category}` : ''}</p>
              </div>
              <button onClick={closePreview} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-5">
              {/* WhatsApp-style bubble */}
              <div>
                <div className="text-xs font-medium text-gray-500 mb-2">Preview</div>
                <div className="rounded-xl p-3" style={{ background: WA_BUBBLE_BG }}>
                  <div className="bg-white rounded-lg rounded-tl-none shadow-sm p-3 max-w-[90%]">
                    {preview.header && (
                      <div className="text-sm font-semibold text-gray-900 mb-1">{preview.header}</div>
                    )}
                    <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                      {renderTemplateBody(preview.body)}
                    </div>
                    {preview.footer && (
                      <div className="text-[11px] text-gray-400 mt-2">{preview.footer}</div>
                    )}
                  </div>
                  {preview.buttons && preview.buttons.length > 0 && (
                    <div className="mt-1.5 space-y-1">
                      {preview.buttons.map((b, i) => (
                        <div key={i} className="bg-white rounded-lg shadow-sm py-2 text-center text-sm text-[#00a5f4] font-medium flex items-center justify-center gap-1.5">
                          {b.type === 'URL' && <ExternalLink className="w-3.5 h-3.5" />}
                          {b.type === 'PHONE_NUMBER' && <Phone className="w-3.5 h-3.5" />}
                          {b.text || b.type}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {isMediaHeader(preview) && (
                <HeaderMediaPanel
                  template={preview}
                  onChange={(url) => patchTemplate(preview.id, url)}
                />
              )}

              {/* Variables + editable sample values. key= remounts on template switch. */}
              <div>
                <SampleValuesPanel key={preview.id} template={preview} onSaved={reloadTemplates} />
                <p className="mt-2 text-[11px] text-gray-400">Parameter mapping to journey data is set on the WhatsApp step inside the journey builder.</p>
              </div>

              {/* Meta info */}
              <div className="text-xs text-gray-500 space-y-1 border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between">
                  <span>Template ID</span>
                  <button onClick={() => copy(preview.provider_template_id)} className="font-mono text-gray-700 flex items-center gap-1 hover:text-brandColor">
                    {preview.provider_template_id} <Copy className="w-3 h-3" />
                  </button>
                </div>
                {preview.approved_at && (
                  <div className="flex items-center justify-between">
                    <span title="Meta last_updated_time — closest proxy for approval date">Approved / updated</span>
                    <span className="text-gray-700">{formatTemplateDate(preview.approved_at)}</span>
                  </div>
                )}
                {preview.last_synced_at && (
                  <div className="flex items-center justify-between">
                    <span>Last synced</span>
                    <span className="text-gray-700">{new Date(preview.last_synced_at).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Delete — soft: hidden from the CRM, still on the WhatsApp account. */}
              <div className="border-t border-gray-100 pt-4">
                {usage[preview.element_name]?.journeys?.length ? (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-3">
                    Used by active journey(s): {usage[preview.element_name].journeys.join(', ')}.
                    Pause them or change the step&apos;s template before deleting.
                  </p>
                ) : confirmDelete ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-xs text-red-800">
                      Delete <b>{preview.element_name}</b>? It will disappear from this page, the
                      inbox and the journey builder. Messages already sent keep showing in chats.
                    </p>
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        onClick={() => setConfirmDelete(false)}
                        disabled={deleting}
                        className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDelete(preview)}
                        disabled={deleting}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" /> Delete template
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppCloudPage;
